<script setup lang="ts">
  import { useField } from 'vee-validate'

  const props = withDefaults(
    defineProps<{
      name: string
      label: string
      accept?: string
      error?: string
      required?: boolean
      value?: File | null
      /** Extra classes for the button (size overrides). */
      buttonClass?: string
    }>(),
    { accept: '', error: '', required: false, value: null, buttonClass: '' }
  )

  const theme = useCareerTheme()

  const input = ref<HTMLInputElement | null>(null)
  const { value, handleChange, setValue } = useField<File | null>(() => props.name)

  const openPicker = () => input.value?.click()
  const onChange = (event: Event) => handleChange((event.target as HTMLInputElement).files?.[0] ?? null)
  const clear = () => {
    handleChange(null)
    if (input.value) input.value.value = ''
  }

  watch(
    () => props.value,
    newValue => {
      if (newValue && !value.value) setValue(newValue)
    },
    { immediate: true }
  )
</script>

<template>
  <div class="min-w-0">
    <button
      class="upload-btn flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 font-bold duration-300"
      type="button"
      :class="[
        props.buttonClass,
        props.error
          ? 'border-red-400 text-red-500'
          : value
            ? 'upload-btn--filled border-viking-400 text-viking-600'
            : 'upload-btn--empty border-zinc-300 text-tarawera-900 hover:border-viking-400 hover:text-viking-500'
      ]"
      :style="theme.primary && !props.error ? { '--upload-accent': theme.primary, '--upload-text': theme.text } : undefined"
      @click="openPicker"
    >
      <span class="min-w-0 truncate">
        {{ value ? value.name : props.label }}<span class="ml-0.5 text-zinc-400" v-if="props.required && !value">*</span>
      </span>
      <Icon class="flex-shrink-0 text-zinc-400 duration-300 hover:text-red-500" v-if="value" name="xmark" @click.stop="clear" />
    </button>
    <input class="hidden" ref="input" type="file" :accept="props.accept" :data-testid="`upload-${props.name}`" @change="onChange" />
    <p class="mt-1 text-sm text-red-500" v-if="props.error">{{ props.error }}</p>
  </div>
</template>

<style scoped>
  .upload-btn--filled {
    border-color: var(--upload-accent, #58d9db);
    color: var(--upload-accent, #0d9488);
  }

  .upload-btn--empty {
    color: var(--upload-text, #1f2937);
  }

  .upload-btn--empty:hover {
    border-color: var(--upload-accent, #58d9db);
    color: var(--upload-accent, #14b8a6);
  }
</style>
